import React, {useMemo} from 'react';
import {CheckTransparent, Copy} from '@sail/helm';
import {Link, css, media, tokens} from '@sail/ui';
import {useIntl} from '@sail/intl';
import {useId} from '@sail/react-aria';
import {List} from 'mkt-ssr/components/sail/List/List';
import {ListItem} from 'mkt-ssr/components/sail/ListItem/ListItem';
import * as messages from 'mkt-ssr/components/sail/pages/StartupPartners/BenefitsList.messages';
import {useLinkResolver} from 'mkt-ssr/lib/routing/LinkResolver';
import {
  StartupPartnersBenefitsPackageFields,
  StartupPartnersPageCurrency,
} from '@stripe-internal/mkt-ssr-models';

type CreateRouteFunction = ReturnType<typeof useLinkResolver>['createRoute'];

export interface BenefitsListProps {
  benefitsTier: StartupPartnersBenefitsPackageFields;
  waivedProcessingFee?: number;
  currency?: StartupPartnersPageCurrency;
  customBenefits?: string[];
  waivedProcessingAmount?: number;
  creditAmount?: number;
}

const urlRegex = /(https?:\/\/[^\s]+)/g;
const allowedDomains = ['stripe.com', 'atlas.stripe.com'];

function isValidHttpUrl(
  url: string,
): url is `https://${string}` | `http://${string}` {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
}

const isAllowedDomain = (url: string): boolean => {
  if (!isValidHttpUrl(url)) {
    return false;
  }

  const domain = new URL(url).hostname;
  return allowedDomains.some((allowedDomain) => domain.endsWith(allowedDomain));
};

const hashString = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash % 65535) * 31 + str.charCodeAt(i)) % 65535;
  }
  return hash.toString(36);
};

export const parseTextWithLinks = (
  text: string,
  createRoute: CreateRouteFunction,
): React.ReactNode => {
  const parts = text.split(urlRegex);

  const parsedParts = parts.map((part) => {
    if (part.match(urlRegex) && isValidHttpUrl(part) && isAllowedDomain(part)) {
      return (
        <Link key={`link-${hashString(part)}`} href={createRoute(part)}>
          {part}
        </Link>
      );
    }
    return part;
  });

  const containsReactElements = parsedParts.some((part) =>
    React.isValidElement(part),
  );

  if (containsReactElements) {
    return <span>{parsedParts}</span>;
  }

  return text;
};

export const BenefitsList: React.FC<
  React.PropsWithChildren<BenefitsListProps>
> = ({
  benefitsTier,
  waivedProcessingFee,
  currency = 'usd',
  customBenefits,
  creditAmount,
  waivedProcessingAmount,
}) => {
  const intl = useIntl();
  const listId = useId();
  const {createRoute} = useLinkResolver();

  const customBenefitIds = useMemo(
    () =>
      customBenefits
        ? customBenefits.map((_, index) => `${listId}-${index}`)
        : [],
    [customBenefits, listId],
  );

  const benefitItems: React.ReactElement<typeof ListItem>[] = [];
  const hasCreditAndProcessingAmount =
    waivedProcessingAmount !== undefined && creditAmount !== undefined;

  if (hasCreditAndProcessingAmount) {
    benefitItems.push(
      <ListItem key="waivedProcessingAmount">
        {intl.formatMessage(messages.waivedProcessingAmountDescription, {
          creditAmount: intl.formatCurrency(
            currency === 'jpy' ? creditAmount : creditAmount * 100,
            {
              currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            },
          ),
          waivedProcessingAmount: intl.formatCurrency(
            currency === 'jpy'
              ? waivedProcessingAmount
              : waivedProcessingAmount * 100,
            {
              currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            },
          ),
        })}
      </ListItem>,
    );
  } else if (waivedProcessingFee !== undefined) {
    benefitItems.push(
      <ListItem key="waivedProcessingFee">
        {intl.formatMessage(messages.waivedProcessingFeeDescription, {
          waivedProcessingFee: intl.formatCurrency(
            currency === 'jpy'
              ? waivedProcessingFee
              : waivedProcessingFee * 100,
            {
              currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            },
          ),
        })}
      </ListItem>,
    );
  }

  if (benefitsTier.freeFirstYear) {
    benefitItems.push(
      <ListItem key="freeFirstYear">
        {intl.formatMessage(messages.firstYearFree)}
      </ListItem>,
    );
  }

  if (benefitsTier.eligibleForCustomPricing) {
    benefitItems.push(
      <ListItem key="eligibleForCustomPricing">
        {intl.formatMessage(messages.eligibleForCustomPricing, {
          eligibleMMRTarget: intl.formatCurrency(15000000, {
            currency: 'usd',
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          }),
        })}
      </ListItem>,
    );
  }

  if (benefitsTier.accessToExclusiveEvents) {
    benefitItems.push(
      <ListItem key="accessToExclusiveEvents">
        {intl.formatMessage(messages.accessToExclusiveEvents)}
      </ListItem>,
    );
  }

  if (benefitsTier.prioritySupport) {
    benefitItems.push(
      <ListItem key="prioritySupport">
        {intl.formatMessage(messages.prioritySupport)}
      </ListItem>,
    );
  }

  if (benefitsTier.discountedAtlasIncorporation) {
    benefitItems.push(
      <ListItem key="discountedAtlasIncorporation">
        {intl.formatMessage(messages.discountedAtlasIncorporation)}
      </ListItem>,
    );
  }

  if (benefitsTier.expertInsightsAndProgramming) {
    benefitItems.push(
      <ListItem key="expertInsightsAndProgramming">
        {intl.formatMessage(messages.expertInsightsAndProgramming)}
      </ListItem>,
    );
  }

  if (benefitsTier.guidanceOnIntegrations) {
    benefitItems.push(
      <ListItem key="guidanceOnIntegrations">
        {intl.formatMessage(messages.guidanceOnIntegrations)}
      </ListItem>,
    );
  }

  if (benefitsTier.exclusiveEvents) {
    benefitItems.push(
      <ListItem key="exclusiveEvents">
        {intl.formatMessage(messages.exclusiveEvents)}
      </ListItem>,
    );
  }

  if (benefitsTier.aPricingReview) {
    benefitItems.push(
      <ListItem key="aPricingReview">
        {intl.formatMessage(messages.aPricingReview, {
          eligibleMMRTarget: intl.formatCurrency(10000000, {
            currency: 'usd',
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          }),
        })}
      </ListItem>,
    );
  }

  if (benefitsTier.offerAvailableToNewStripeUsersOnly) {
    benefitItems.push(
      <ListItem key="offerAvailableToNewStripeUsersOnly">
        {intl.formatMessage(messages.offerAvailableToNewStripeUsersOnly)}
      </ListItem>,
    );
  }

  if (customBenefits) {
    customBenefits.forEach((benefit, index) => {
      benefitItems.push(
        <ListItem key={customBenefitIds[index]}>
          {parseTextWithLinks(benefit, createRoute)}
        </ListItem>,
      );
    });
  }

  return (
    <Copy
      variant="detail"
      title={intl.formatMessage(messages.sectionTitle)}
      uses={[
        css({
          marginTop: 'space.0',
          [media.up(tokens.viewport.large)]: {
            marginTop: 'space.1000',
          },
        }),
      ]}
    >
      <List defaultIcon={CheckTransparent}>{benefitItems}</List>
      {hasCreditAndProcessingAmount ? (
        <Link
          css={{font: 'body.medium', fontWeight: 'semibold'}}
          href={createRoute(
            'https://support.stripe.com/questions/stripe-startups-program-faqs',
          )}
        >
          {intl.formatMessage(messages.faqCta)}
        </Link>
      ) : null}
    </Copy>
  );
};
